import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import InputField from '../../../../Components/Form/components/InputField';
import DateField from '../../../../Components/Form/components/DateField';
import HealthInsuranceCompanySelectField from '../../../Base/Components/Inputs/HealthInsuranceCompanySelect/HealthInsuranceCompanySelectField';
import GenderSelectField from '../../../Base/Components/Inputs/GenderSelect/GenderSelectField';
import DiagnosisSelectField from '../../../../Components/Base/Diagnosis/DiagnosisSelectField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import { Moment } from 'moment';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { usePatientProfileDataQuery } from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TimePicker from '../../../../Components/Base/TimePicker/TimePicker';
import FormControl from '../../../../Components/Form/FormControl';
import RadioListField from '../../../../Components/Form/components/RadioListField';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import FormContainer from '../../../../Components/Form/FormContainer';
import NumberField from '../../../../Components/Form/components/NumberField';
import { useValidationMessage } from '../../../Base/Hooks/useValidationMessage';
import DoctorSelectField from '../../../../Components/Form/components/DoctorSelectField';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import PhoneNumberField from '../../../../Components/PhoneNumber/PhoneNumberField';

interface Props {
	onSuccess: (values: LaboratoryRequestCreateRequestHeaderFormValues) => void;
	patientId: string;
}

export interface LaboratoryRequestCreatePatient {
	firstName: string;
	lastName: string;
	insuranceNumber: string;
	birthDate: Moment;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber?: string;
		city: string;
		postCode: string;
	};
	insuranceCompany: SelectValue;
	gender: SelectValue;
	diagnosis: SelectValue;
}

export interface LaboratoryRequestCreateDocumentInfo {
	tookBy?: SelectValue;
	tookByCustom?: string;
	date?: Moment;
	time?: Moment;
	primarySampleType: string;
	statim: boolean;
	selfPaid: boolean;
	statimPhoneNumber?: string;
}

export interface LaboratoryRequestCreateRequestHeaderFormValues {
	patient: LaboratoryRequestCreatePatient;
	documentInfo: LaboratoryRequestCreateDocumentInfo;
}

const LaboratoryRequestCreateRequestHeaderForm: React.FC<Props> = ({
	onSuccess,
	patientId,
}) => {
	const [isWorkerOutsideOfMEDDI, setIsWorkerOutsideMEDDI] = useState(false);
	const { getMessageByKey } = useValidationMessage(
		'validation.laboratoryRequestCreateRequestHeaderForm',
	);

	const validationSchema = yup.object({
		patient: yup.object({
			firstName: yup.string().required(getMessageByKey('firstName')),
			lastName: yup.string().required(getMessageByKey('lastName')),
			insuranceNumber: yup
				.string()
				.required(getMessageByKey('insuranceNumber')),
			birthDate: yup.date().required(getMessageByKey('birthDate')),
			address: yup.object({
				street: yup.string().required(getMessageByKey('street')),
				descriptiveNumber: yup
					.string()
					.required(getMessageByKey('descriptiveNumber')),
				guideNumber: yup.string().notRequired(),
				city: yup.string().required(getMessageByKey('city')),
				postCode: yup.string().required(getMessageByKey('postCode')),
			}),
			insuranceCompany: yup.object({
				label: yup.string(),
				value: yup.string().required(getMessageByKey('insuranceCompany')),
			}),
			gender: yup
				.object({
					label: yup.string(),
					value: yup.string(),
				})
				.typeError(getMessageByKey('gender'))
				.required(getMessageByKey('gender')),
			diagnosis: yup
				.object({
					label: yup.string(),
					value: yup.string(),
				})
				.required(getMessageByKey('diagnosis')),
		}),
		documentInfo: yup.object({
			primarySampleType: yup
				.mixed()
				.nullable()
				.required(getMessageByKey('primarySampleType')),
		}),
	});

	const { data, isLoading } = usePatientProfileDataQuery({ userId: patientId });
	const formMethods = useForm({ resolver: yupResolver(validationSchema) });
	const handleSubmit = (values: any) => {
		onSuccess(values as LaboratoryRequestCreateRequestHeaderFormValues);
	};

	useEffect(() => {
		const user = data?.user;
		const userProfile = user?.profile;

		const diagnosis = user?.meta
			?.find(metaItem => metaItem?.key === 'diagnosis')
			?.value?.find(
				(diagnosis: { label: string; value: string; subType: string }) =>
					diagnosis?.subType === 'main_diagnosis',
			);

		if (userProfile) {
			formMethods.reset({
				patient: {
					firstName: userProfile.firstName,
					lastName: userProfile.lastName,
					insuranceNumber: userProfile.insuranceNumber,
					birthDate: userProfile.birthDate,
					address: {
						street: userProfile.address?.street,
						descriptiveNumber: userProfile.address?.descriptiveNumber,
						guideNumber: userProfile.address?.guideNumber,
						city: userProfile.address?.city,
						postCode: userProfile.address?.postCode,
					},
					insuranceCompany: userProfile.insuranceCompanyTermItem?.id,
					gender: userProfile.gender,
					diagnosis: {
						label: diagnosis?.label,
						value: diagnosis?.value,
					},
				},
			});
		}
	}, [data]);

	const STATIMValue = formMethods.watch('documentInfo.statim');

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div>
			<FormWrapper formMethods={formMethods}>
				<FormContainer name='patient'>
					<div className='row'>
						<div className='col-6'>
							<InputField
								label='component.laboratoryRequestCreateRequestHeaderForm.firstName'
								name='firstName'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-6'>
							<InputField
								label='component.laboratoryRequestCreateRequestHeaderForm.lastName'
								name='lastName'
								isWrapped
								isRequired
							/>
						</div>
					</div>
					<div className='row'>
						<div className='col-6'>
							<InputField
								label='component.laboratoryRequestCreateRequestHeaderForm.insuranceNumber'
								name='insuranceNumber'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-6'>
							<DateField
								label='component.laboratoryRequestCreateRequestHeaderForm.birthDate'
								name='birthDate'
								isRequired
							/>
						</div>
					</div>
					<FormContainer name='address'>
						<div className='row'>
							<div className='col-4'>
								<InputField
									label='component.laboratoryRequestCreateRequestHeaderForm.street'
									name='street'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<NumberField
									label='component.laboratoryRequestCreateRequestHeaderForm.descriptiveNumber'
									name='descriptiveNumber'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<NumberField
									label='component.laboratoryRequestCreateRequestHeaderForm.guideNumber'
									name='guideNumber'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<InputField
									label='component.laboratoryRequestCreateRequestHeaderForm.city'
									name='city'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<NumberField
									label='component.laboratoryRequestCreateRequestHeaderForm.postCode'
									name='postCode'
									isRequired
								/>
							</div>
						</div>
					</FormContainer>
					<div className='row'>
						<div className='col-3'>
							<HealthInsuranceCompanySelectField
								label='component.laboratoryRequestCreateRequestHeaderForm.insuranceCompany'
								name='insuranceCompany'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-3'>
							<GenderSelectField
								name='gender'
								label='component.laboratoryRequestCreateRequestHeaderForm.gender'
								valueKey='id'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-6'>
							<DiagnosisSelectField
								name='diagnosis'
								label='component.laboratoryRequestCreateRequestHeaderForm.diagnosis'
								isRequired
							/>
						</div>
					</div>
				</FormContainer>
				<FormContainer name='documentInfo'>
					<div className='row'>
						<div className='col-6'>
							{isWorkerOutsideOfMEDDI ? (
								<InputField
									label='component.laboratoryRequestCreateRequestHeaderForm.tookBy'
									name='tookByCustom'
								/>
							) : (
								<InputWrapper>
									<DoctorSelectField
										label='component.laboratoryRequestCreateRequestHeaderForm.tookBy'
										name='tookBy'
									/>
									<LinkButton
										title='component.laboratoryRequestCreateRequestHeaderForm.tookByCustomToggle'
										theme={Theme.BLUE}
										onClick={() => setIsWorkerOutsideMEDDI(true)}
									/>
								</InputWrapper>
							)}
						</div>
						<div className='col-3'>
							<DateField
								label='component.laboratoryRequestCreateRequestHeaderForm.date'
								name='date'
							/>
						</div>
						<div className='col-3'>
							<FormControl
								label='component.laboratoryRequestCreateRequestHeaderForm.time'
								name='time'>
								<TimePicker />
							</FormControl>
						</div>
					</div>
					<div className='row'>
						<div className='col-4'>
							<RadioListField
								label='component.laboratoryRequestCreateRequestHeaderForm.primarySampleType'
								name='primarySampleType'
								isRequired
								isWrapped
								data={[
									{
										title:
											'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.blood',
										value: 'blood',
									},
									{
										title:
											'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.urine',
										value: 'urine',
									},
									{
										title:
											'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.spit',
										value: 'spit',
									},
									{
										title:
											'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.stool',
										value: 'stool',
									},
								]}
								inLine
							/>
						</div>
						<div className='col-2 pt-3'>
							<CheckboxField name='statim' tx='STATIM' isRequired isWrapped />
							{STATIMValue && (
								<PhoneNumberField
									name='statimPhoneNumber'
									label='component.laboratoryRequestCreateRequestHeaderForm.statimPhoneNumber'
								/>
							)}
						</div>
						<div className='col-2 pt-3'>
							<CheckboxField
								name='selfPaid'
								tx='component.laboratoryRequestCreateRequestHeaderForm.selfPaid'
								isRequired
								isWrapped
							/>
						</div>
					</div>
				</FormContainer>
				<div className='mt-3'>
					<SubmitButton onSubmit={handleSubmit} title='base.shared.continue' />
				</div>
			</FormWrapper>
		</div>
	);
};

export default LaboratoryRequestCreateRequestHeaderForm;
